import React, { useState, useEffect, useContext } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { AccountsContext } from "../../../components/context/account-context";

// Components
import VehicleViewHeader from "./components/VehicleViewHeader";
import VehicleViewTabs from "./components/VehicleViewTabs";
import VehicleViewFilters from "./components/VehicleViewFilters";
import VehicleViewModals from "./components/VehicleViewModals";
import VehicleViewTabsConfig from "./components/VehicleViewTabsConfig";

// Hooks
import { useVehicleViewData } from "./hooks/useVehicleViewData";
import { useVehicleViewMedia } from "./hooks/useVehicleViewMedia";
import { useVehicleViewModals } from "./hooks/useVehicleViewModals";
import Help from './help';
import { usePageHelp } from '../../../components/context/help-context';

const MORE_OPTIONS = [
    { name: "Media", label: "Media" },
    // { name: "Settings", label: "Settings" },
    { name: "ServiceConfig", label: "Service configuration" },
];

const VehicleViews = () => {
    usePageHelp(Help);
    const { accountId, attributes } = useContext(AccountsContext);
    const { vehicleId } = useParams();
    const { collapsed } = useOutletContext();

    // State
    const [activeTab, setActiveTab] = useState("Overview");
    const [isMoreOpen, setIsMoreOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [dateRange, setDateRange] = useState({
        startDate: new Date(new Date().setDate(new Date().getDate() - 7)),
        endDate: new Date(),
    });

    // Custom hooks
    const {
        vehicle,
        setVehicle,
        vehicleError,
        loading,
        fuelData,
        setFuelData,
        expenseData,
        setExpenseData,
        IncidentData,
        setIncidentData,
        meterData,
        setMeterData,
        setTireData,
        tireData,
        tirePositions,
        setTirePositions,
        tireUnAssign,
        setTireUnAssign,
        users,
        vehicles,
        serviceData,
        setServiceData,
        reminderData,
        setReminderData,
        serviceConfigData,
        setServiceConfigData,
        renewalData,
        setRenewalData,
        renewalTypes,
        setRenewalTypes,
        meterTypes,
        setMeterTypes,
        vendors,
        categories,
        paymentMethods,
        jobsOptions,
        typesOptions,
        serviceTypesOptions,
        serviceConfigTypesOptions,
        tabStates,
        fetchTabData,
    } = useVehicleViewData(dateRange);


    const {
        mediaLoading,
        media,
        mediaError,
        newlyAddedItem,
        setNewlyAddedItem,
        fileInputRef,
        handleFileSelect,
        handleAddMediaClick,
        handleRemoveImage,
    } = useVehicleViewMedia();

    const {
        openFuelAddModal,
        openMeterAddModal,
        openRenewalAddModal,
        openIncidentAddModal,
        openExpenseAddModal,
        openServicesAddModal,
        openServiceConfigAddModal,
        setOpenServiceConfigAddModal,
        handleOpenAddModal,
        handleCloseAddModal,
        handleAddItem,
        handleOpenEditModal,
    } = useVehicleViewModals();

    // Effects
    useEffect(() => {
        if (["Fuels", "Meters", "Expenses", "Services", "Renewals", "Incidents", "Tires", "Reminders", "ServiceConfig"].includes(activeTab)) {
            fetchTabData(activeTab);
        }
    }, [activeTab, dateRange]);

    // Event handlers
    const handleMoreClick = (e) => {
        e.stopPropagation();
        setIsMoreOpen((prev) => !prev);
    };

    const handleMoreOptionClick = (optionName) => {
        setActiveTab(optionName);
        setIsMoreOpen(false);
    };

    const handleTabClick = (tabName) => {
        setActiveTab(tabName);
        if (MORE_OPTIONS.some((option) => option.name === tabName)) {
            setIsMoreOpen(false);
        }
    };

    const handleOpenAddModalWrapper = () => {
        handleOpenAddModal(activeTab, handleAddMediaClick);
    };

    const handleAddItemWrapper = (newItem) => {
        handleAddItem(newItem, setNewlyAddedItem);
    };

    const handleOpenEditModalWrapper = (item) => {
        handleOpenEditModal(item, setNewlyAddedItem);
    };

    const isMoreActive = MORE_OPTIONS.some((option) => option.name === activeTab);

    // Get tabs configuration
    const TABS = VehicleViewTabsConfig({
        vehicle,
        vehicleId,
        accountId,
        searchTerm,
        setSearchTerm,
        newlyAddedItem,
        setNewlyAddedItem,
        fuelData,
        setFuelData,
        meterData,
        setMeterData,
        setTireData,
        tireData,
        tirePositions,
        setTirePositions,
        tireUnAssign,
        setTireUnAssign,
        expenseData,
        setExpenseData,
        IncidentData,
        setIncidentData,
        tabStates,
        fetchTabData,
        meterTypes,
        setMeterTypes,
        users,
        serviceData,
        reminderData,
        setReminderData,
        setServiceData,
        serviceConfigData,
        setServiceConfigData,
        renewalData,
        setRenewalData,
        renewalTypes,
        setRenewalTypes,
        vehicles,
        vendors,
        categories,
        paymentMethods,
        jobsOptions,
        typesOptions,
        serviceTypesOptions,
        serviceConfigTypesOptions,
        media,
        mediaError,
        handleRemoveImage,
        collapsed
    });


    const activeTabContent = TABS.find((tab) => tab.name === activeTab)?.component || (
        <div className="p-4">No Content Available</div>
    );

    return (
        <div className="p-4 bg-white min-h-screen relative">
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="image/*, .pdf, .doc, .docx, .xls, .xlsx, .ppt, .pptx, .txt, .csv"
                className="hidden"
            />

            <VehicleViewHeader
                vehicleError={vehicleError}
                mediaError={mediaError}
                loading={loading}
                vehicle={vehicle}
                vehicleId={vehicleId}
                accountId={accountId}
                setVehicle={setVehicle}
                activeTab={activeTab}
                mediaLoading={mediaLoading}
                handleOpenAddModal={handleOpenAddModalWrapper}
            />

            <VehicleViewTabs
                TABS={TABS}
                activeTab={activeTab}
                isMoreOpen={isMoreOpen}
                setIsMoreOpen={setIsMoreOpen}
                handleTabClick={handleTabClick}
                handleMoreClick={handleMoreClick}
                handleMoreOptionClick={handleMoreOptionClick}
                isMoreActive={isMoreActive}
            />

            <VehicleViewFilters
                activeTab={activeTab}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                dateRange={dateRange}
                setDateRange={setDateRange}
            />

            <div className="mt-3">
                <div className="px-0 bg-white">{activeTabContent}</div>
            </div>

            <VehicleViewModals
                activeTab={activeTab}
                openFuelAddModal={openFuelAddModal}
                openMeterAddModal={openMeterAddModal}
                openRenewalAddModal={openRenewalAddModal}
                openIncidentAddModal={openIncidentAddModal}
                openExpenseAddModal={openExpenseAddModal}
                openServicesAddModal={openServicesAddModal}
                openServiceConfigAddModal={openServiceConfigAddModal}
                setOpenServiceConfigAddModal={setOpenServiceConfigAddModal}
                handleCloseAddModal={handleCloseAddModal}
                handleAddItem={handleAddItemWrapper}
                handleOpenEditModal={handleOpenEditModalWrapper}
                vehicle={vehicle}
                users={users}
                meterTypes={meterTypes}
                vehicles={vehicles}
                vendors={vendors}
                renewalTypes={renewalTypes}
                fetchTabData={fetchTabData}
                typesOptions={typesOptions}
                serviceTypesOptions={serviceTypesOptions}
                serviceConfigTypesOptions={serviceConfigTypesOptions}
                accountId={accountId}
                categories={categories}
                paymentMethods={paymentMethods}
                jobsOptions={jobsOptions}
            />
        </div>
    );
};

export default VehicleViews;